import apiClient from './client'

import type {
  InventarioCategoria,
  InventarioUbicacion,
  InventarioItem,
  InventarioItemListItem,
  InventarioItemInput,
  ItemMovimiento,
  ItemMovimientoInput,
  PaginatedResponse
} from '@/types/gia'

interface InventarioFilters {
  page?: number
  page_size?: number
  centro?: number
  categoria?: number
  estado?: string
  persona?: number
  ubicacion?: number
  barcode?: string
  q?: string
}

const inventarioApi = {
  // ---- Items CRUD ----
  list: async (filters: InventarioFilters = {}): Promise<PaginatedResponse<InventarioItemListItem>> => {
    const response = await apiClient.get<PaginatedResponse<InventarioItemListItem>>('/inventario/', {
      params: filters
    })

    return response.data
  },

  get: async (id: number): Promise<InventarioItem> => {
    const response = await apiClient.get<InventarioItem>(`/inventario/${id}/`)

    return response.data
  },

  create: async (data: InventarioItemInput): Promise<InventarioItem> => {
    const response = await apiClient.post<InventarioItem>('/inventario/', data)

    return response.data
  },

  update: async (id: number, data: Partial<InventarioItemInput>): Promise<InventarioItem> => {
    const response = await apiClient.patch<InventarioItem>(`/inventario/${id}/`, data)

    return response.data
  },

  delete: async (id: number) => {
    await apiClient.delete(`/inventario/${id}/`)
  },

  uploadPhoto: async (id: number, file: File): Promise<{ foto: string }> => {
    const formData = new FormData()

    formData.append('foto', file)

    const response = await apiClient.post<{ foto: string }>(`/inventario/${id}/photo/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response.data
  },

  getMovimientos: async (id: number): Promise<ItemMovimiento[]> => {
    const response = await apiClient.get<ItemMovimiento[]>(`/inventario/${id}/movimientos/`)

    return response.data
  },

  regenerarQr: async (id: number): Promise<{ qr_code: string }> => {
    const response = await apiClient.post<{ qr_code: string }>(`/inventario/${id}/regenerar_qr/`)

    return response.data
  },

  lookupBarcode: async (barcode: string): Promise<PaginatedResponse<InventarioItemListItem>> => {
    const response = await apiClient.get<PaginatedResponse<InventarioItemListItem>>('/inventario/', {
      params: { barcode }
    })

    return response.data
  },

  // ---- Categorias ----
  listCategorias: async (): Promise<InventarioCategoria[]> => {
    const response = await apiClient.get<InventarioCategoria[]>('/inventario-categorias/')

    return response.data
  },

  createCategoria: async (data: Partial<InventarioCategoria>): Promise<InventarioCategoria> => {
    const response = await apiClient.post<InventarioCategoria>('/inventario-categorias/', data)

    return response.data
  },

  updateCategoria: async (id: number, data: Partial<InventarioCategoria>): Promise<InventarioCategoria> => {
    const response = await apiClient.put<InventarioCategoria>(`/inventario-categorias/${id}/`, data)

    return response.data
  },

  deleteCategoria: async (id: number) => {
    await apiClient.delete(`/inventario-categorias/${id}/`)
  },

  // ---- Ubicaciones ----
  listUbicaciones: async (params: { centro?: number } = {}): Promise<InventarioUbicacion[]> => {
    const response = await apiClient.get<InventarioUbicacion[]>('/inventario-ubicaciones/', { params })

    return response.data
  },

  createUbicacion: async (data: { centro: number; nombre: string; descripcion?: string }): Promise<InventarioUbicacion> => {
    const response = await apiClient.post<InventarioUbicacion>('/inventario-ubicaciones/', data)

    return response.data
  },

  updateUbicacion: async (id: number, data: Partial<InventarioUbicacion>): Promise<InventarioUbicacion> => {
    const response = await apiClient.patch<InventarioUbicacion>(`/inventario-ubicaciones/${id}/`, data)

    return response.data
  },

  deleteUbicacion: async (id: number) => {
    await apiClient.delete(`/inventario-ubicaciones/${id}/`)
  },

  // ---- Movimientos ----
  listMovimientos: async (params: { item?: number; tipo?: string } = {}): Promise<PaginatedResponse<ItemMovimiento>> => {
    const response = await apiClient.get<PaginatedResponse<ItemMovimiento>>('/inventario-movimientos/', { params })

    return response.data
  },

  createMovimiento: async (data: ItemMovimientoInput): Promise<ItemMovimiento> => {
    const response = await apiClient.post<ItemMovimiento>('/inventario-movimientos/', data)

    return response.data
  }
}

export default inventarioApi
